import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { Dropdown } from "../ui/dropdown/Dropdown";
import { DropdownItem } from "../ui/dropdown/DropdownItem";
import { ChevronDownIcon } from "../../icons";
import { useEffect, useState, useMemo } from "react";
import { request } from "../../utils";

interface Order {
  createdAt: string;
  status: string;
  [key: string]: any;
}

export default function MonthlyOrdersChart() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear()
  );

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  useEffect(() => {
    const loadOrders = async () => {
      try {
        const response = await request.get({
          url: "/order/my-orders",
        });

        const ordersData = Array.isArray(response.data) ? response.data : [];
        setOrders(ordersData);
      } catch (err) {
        console.error("Failed to load orders:", err);
        setError("Failed to load order data");
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
  }, []);

  // Get all available years from orders
  const availableYears = useMemo(() => {
    const years = new Set<number>();
    orders.forEach((order) => {
      const date = new Date(order.createdAt);
      years.add(date.getFullYear());
    });
    return Array.from(years).sort((a, b) => b - a); // Sort descending (newest first)
  }, [orders]);

  // Update selected year to the most recent year if not available
  useEffect(() => {
    if (availableYears.length > 0 && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  // Process orders data to count by month for selected year
  const processMonthlyData = () => {
    const monthlyCounts = monthNames.map(() => 0);

    orders.forEach((order) => {
      const date = new Date(order.createdAt);
      const year = date.getFullYear();
      const month = date.getMonth(); // 0-11 (Jan-Dec)

      if (year === selectedYear && month >= 0 && month < 12) {
        monthlyCounts[month]++;
      }
    });

    return monthlyCounts;
  };

  const chartOptions: ApexOptions = {
    colors: ["#465fff"],
    chart: {
      fontFamily: "Outfit, sans-serif",
      type: "bar",
      height: 180,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "39%",
        borderRadius: 5,
        borderRadiusApplication: "end",
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 4,
      colors: ["transparent"],
    },
    xaxis: {
      categories: monthNames,
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    legend: {
      show: true,
      position: "top",
      horizontalAlign: "left",
      fontFamily: "Outfit",
    },
    yaxis: {
      title: {
        text: undefined,
      },
    },
    grid: {
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    fill: {
      opacity: 1,
    },
    tooltip: {
      x: {
        show: false,
      },
      y: { 
        formatter: (val: number) => `${val} exchange` ,
      },
    },
  };

  const chartSeries = [
    {
      name: "Exchanges",
      data: processMonthlyData(),
    },
  ];

  function toggleMenu() {
    setIsMenuOpen(!isMenuOpen);
  }

  function toggleYearDropdown() {
    setIsYearDropdownOpen(!isYearDropdownOpen);
  }

  function closeYearDropdown() {
    setIsYearDropdownOpen(false);
  }

  function handleYearSelect(year: number) {
    setSelectedYear(year);
    closeYearDropdown();
  }

  if (loading) {
    return (
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white px-5 pt-5 dark:border-gray-800 dark:bg-white/[0.03] sm:px-6 sm:pt-6">
        <div className="h-6 w-1/3 bg-gray-100 rounded dark:bg-gray-800 mb-4"></div>
        <div className="h-40 bg-gray-100 rounded dark:bg-gray-800"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white px-5 pt-5 dark:border-gray-800 dark:bg-white/[0.03] sm:px-6 sm:pt-6 text-red-500">
        {error}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white px-5 pt-5 dark:border-gray-800 dark:bg-white/[0.03] sm:px-6 sm:pt-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
          Monthly Exchange Request
        </h3>

        <div className="flex items-center gap-3">
          {/* Year Selector Dropdown */}
          <div className="relative inline-block">
            <button
              className="flex items-center gap-1 px-3 py-1 text-sm border border-gray-200 rounded-lg text-gray-700 dark:border-gray-700 dark:text-gray-100"
              onClick={toggleYearDropdown}
            >
              {selectedYear}
              <ChevronDownIcon className="size-4" />
            </button>
            <Dropdown
              isOpen={isYearDropdownOpen}
              onClose={closeYearDropdown}
              className="w-32 p-2 max-h-60 overflow-y-auto"
            >
              {availableYears.map((year) => (
                <DropdownItem
                  key={year}
                  onItemClick={() => handleYearSelect(year)}
                  className={`flex w-full font-normal text-left 
            text-gray-700 dark:text-gray-200 
            rounded-lg 
            hover:bg-gray-100 hover:text-gray-900 
            dark:hover:bg-white/10 dark:hover:text-white 
            ${year === selectedYear ? "bg-gray-100 dark:bg-white/10" : ""}`}
                >
                  {year}
                </DropdownItem>
              ))}
            </Dropdown>
          </div>

          {/* Options Dropdown */}
          <div className="relative inline-block">
            <button className="dropdown-toggle" onClick={toggleMenu}></button>
          </div>
        </div>
      </div>

      <div className="max-w-full overflow-x-auto custom-scrollbar">
        <div className="-ml-5 min-w-[650px] xl:min-w-full pl-2">
          <Chart
            options={chartOptions}
            series={chartSeries}
            type="bar"
            height={180}
          />
        </div>
      </div>
    </div>
  );
}
